import type { Quake } from '../api/types';

// eventThesis builds a single deterministic sentence per event. The
// goal is not beauty; it is making the dashboard useful by turning a
// row of numbers into a one-line read. Pure rules, no LLM. The
// template picks the strongest mover, the dominant direction, and the
// agreement share, then frames the whole thing as the scanner would.

export interface EventThesisInput {
  eventSlug: string;
  eventTitle?: string;
  movers: Quake[];
  dominantDirection: 'up' | 'down' | 'flat';
  agreementPct: number;
}

export function eventThesis(input: EventThesisInput): string {
  const { eventSlug, movers, dominantDirection, agreementPct } = input;
  const eventTitle = input.eventTitle ?? eventDisplayTitle(eventSlug, movers);
  if (movers.length === 0) {
    return `No confirmed movers for ${eventTitle}.`;
  }
  const sortedByScore = [...movers].sort((a, b) => b.score - a.score);
  const top = sortedByScore[0];
  if (!top) return `No confirmed movers for ${eventTitle}.`;

  const dirWord = directionWord(dominantDirection, movers);
  const n = movers.length;
  const noun = n === 1 ? 'market' : 'markets';
  const agree = Math.round(agreementPct);
  const topMove = formatPp(top.change_pp);
  const topOutcome = top.outcome || 'YES';

  // The thesis is one sentence, plain English, no marketing voice.
  return (
    `${eventTitle} ${dirWord}: ${n} ${noun} moving, ${agree}% agreement, ` +
    `strongest move ${topOutcome} ${topMove}.`
  );
}

export function eventDisplayTitle(eventSlug: string, movers: readonly Quake[]): string {
  const sorted = [...movers].sort((a, b) => b.score - a.score);
  for (const mover of sorted) {
    const title = titleFromQuestion(mover.question);
    if (title) return title;
  }

  const common = commonQuestionPrefix(sorted.map((mover) => mover.question));
  if (common) return common;

  const slugTitle = titleFromSlug(eventSlug);
  return slugTitle || eventSlug || 'Untitled event';
}

export function marketDisplayLabel(question: string, eventTitle: string): string {
  const cleanedQuestion = cleanText(question);
  const cleanedTitle = cleanText(eventTitle);
  if (!cleanedQuestion || !cleanedTitle) return cleanedQuestion || question;

  const variants = [
    cleanedTitle,
    cleanedTitle.replace(/\bvs\.\b/i, 'vs'),
    cleanedTitle.replace(/\bvs\b/i, 'vs.'),
  ];

  for (const variant of variants) {
    if (cleanedQuestion.toLowerCase().startsWith(variant.toLowerCase())) {
      const label = cleanedQuestion.slice(variant.length).replace(/^[\s:|.\-]+/, '').trim();
      if (label.length >= 2) return label;
    }
  }

  return cleanedQuestion;
}

function directionWord(direction: 'up' | 'down' | 'flat', movers: Quake[]): string {
  if (direction === 'flat') return 'is mixed';
  if (direction === 'up') {
    const ups = movers.filter((m) => m.direction === 'up').length;
    const allUp = ups === movers.length;
    return allUp ? 'is repricing higher' : 'is leaning higher';
  }
  const downs = movers.filter((m) => m.direction === 'down').length;
  const allDown = downs === movers.length;
  return allDown ? 'is repricing lower' : 'is leaning lower';
}

function formatPp(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}pp`;
}

function titleFromQuestion(question: string): string {
  const cleaned = cleanTitle(question);
  if (!cleaned) return '';

  for (const separator of [':', ' | ', ' - ']) {
    const index = cleaned.indexOf(separator);
    if (index >= 5) return cleanTitle(cleaned.slice(0, index));
  }

  const marketDescriptor = /\s+(?:O\/U|Over\/Under|Total(?:s)?|Spread|Moneyline)\b/i.exec(cleaned);
  if (marketDescriptor?.index && marketDescriptor.index >= 5) {
    return cleanTitle(cleaned.slice(0, marketDescriptor.index));
  }

  return '';
}

function commonQuestionPrefix(questions: string[]): string {
  const tokenized = questions
    .map((question) => cleanTitle(question).split(/\s+/).filter(Boolean))
    .filter((parts) => parts.length > 0);
  if (tokenized.length < 2) return '';

  const prefix: string[] = [];
  const first = tokenized[0] ?? [];
  for (let index = 0; index < first.length; index += 1) {
    const token = first[index];
    if (!token) break;
    const same = tokenized.every((parts) => (parts[index] ?? '').toLowerCase() === token.toLowerCase());
    if (!same) break;
    prefix.push(token);
  }

  const title = cleanTitle(prefix.join(' '));
  if (title.split(/\s+/).length < 2) return '';
  return title;
}

const countryCodes: Record<string, string> = {
  arg: 'Argentina',
  bra: 'Brazil',
  eng: 'England',
  esp: 'Spain',
  fra: 'France',
  ger: 'Germany',
  ksa: 'Saudi Arabia',
  mex: 'Mexico',
  uru: 'Uruguay',
  ury: 'Uruguay',
  usa: 'United States',
};

function titleFromSlug(slug: string): string {
  const parts = slug
    .toLowerCase()
    .split('-')
    .filter((part) => part && !/^\d{2,4}$/.test(part) && part !== 'more' && part !== 'markets');
  const countryIndex = parts.findIndex((part, index) => countryCodes[part] && countryCodes[parts[index + 1] ?? '']);
  if (countryIndex >= 0) {
    const first = countryCodes[parts[countryIndex] ?? ''];
    const second = countryCodes[parts[countryIndex + 1] ?? ''];
    if (first && second) return `${first} vs. ${second}`;
  }
  return parts.map((part) => countryCodes[part] ?? capitalize(part)).join(' ');
}

function cleanTitle(value: string): string {
  return cleanText(value)
    .replace(/^will\s+/i, '')
    .replace(/\?$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function capitalize(value: string): string {
  if (!value) return '';
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}
